import { useReactiveVar } from '@apollo/client';
import { Icon, Stack, Text, TooltipHost } from '@fluentui/react';
import React, { useEffect, useRef } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { TravelerAuthorizationCommonData } from 'travelAuthorization/TravelPlan/view/FormView/__generated__/TravelerAuthorizationCommonData';
import { SectionProps } from '../..';
import { Trip_trip } from '../../__generated__/Trip';
import { TripCreateFormValues, TripItemFormValues } from '../../types';
import { setSelectedTraveler } from '../BasicForm/TravelerAutoComplete';
import { useStyles } from './index.styles';
import { getInitialValues, useTravelIcon } from './utils';
import { TravelAuthorization_travelAuthorization } from 'travelAuthorization/TravelPlan/view/__generated__/TravelAuthorization';
import { getDefaultDistribution } from '../../utils';
import { setUserDefaults } from 'utility/cache/ui';

export const SectionToggle: React.FC<{
  inputsDisabled: boolean;
  tripItemsState: Map<string, SectionProps>;
  onTripItemsUpdate: (
    mapData: Map<string, SectionProps>,
    layoutAdded: string,
    adding: boolean
  ) => void;
  commonData: TravelerAuthorizationCommonData | undefined;
  tripData: Trip_trip | null | undefined;
  travelAuthorizationData:
  | Partial<TravelAuthorization_travelAuthorization>
  | null
  | undefined;
}> = ({
  tripItemsState,
  onTripItemsUpdate,
  commonData,
  inputsDisabled,
  travelAuthorizationData,
  tripData,
}) => {
    const userDefaults = useReactiveVar(setUserDefaults);
    const { append } = useFieldArray({ name: 'tripItems' });
    const { control, setValue } = useFormContext<TripCreateFormValues>();
    const tripItemsValues = useWatch({ name: 'tripItems', control });
    const styles = useStyles();
    const { getIcon, getOutlineIcon } = useTravelIcon();
    const selectedTraveler = useReactiveVar(setSelectedTraveler);

    const tooltipContent = (item: SectionProps, added: boolean) => {
      return (
        <Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign="center">
          {!added && <Icon iconName="add" />}
          <Text> {`${item.name}`}</Text>
        </Stack>
      );
    };

    const DEFAULT_GROUND_TRANSPORT_FIELDS = [
      'pickupDropName',
      'pickupArrivalName',
      'returnDropName',
      'returnArrivalName',
      'departureDate',
      'departureArrivalDate',
      'returnDate',
      'returnArrivalDate',
      'pickupName',
      'returnArrivalDropName',
    ] as const;

    const groundTransportationDefaults = getInitialValues(
      'TRAVEL_BOOKING_GROUND_TRANS',
      selectedTraveler,
      travelAuthorizationData,
      tripItemsValues
    ) as TripItemFormValues;

    const userCleared = useRef<Set<string>>(new Set());
    const prevGroundTransportFields = useRef<Record<string, unknown>>({});
    const prevGroundDefaults = useRef<Record<string, unknown>>({});

    useEffect(() => {
      const hasGroundTransportFields = tripItemsValues?.some(
        (i) => i.layout === 'TRAVEL_BOOKING_GROUND_TRANS'
      );
      if (!hasGroundTransportFields) {
        userCleared.current.clear();
        prevGroundTransportFields.current = {};
        prevGroundDefaults.current = {};
        return;
      }

      tripItemsValues?.forEach((item, index) => {
        if (item.layout !== 'TRAVEL_BOOKING_GROUND_TRANS') return;

        DEFAULT_GROUND_TRANSPORT_FIELDS.forEach((field) => {
          const key = `${index}.${field}`;
          const value = (item[field] as unknown) || null;
          const next = (groundTransportationDefaults[field] as unknown) ?? null;
          const isFirstRun = !(key in prevGroundDefaults.current);
          const sourceChanged =
            !isFirstRun && prevGroundDefaults.current[key] !== next;

          if (value) {
            // Has a value: never overwrite
            userCleared.current.delete(key);
          } else {
            if (sourceChanged) {
              // Empty + source changed: allow refill
              userCleared.current.delete(key);
            } else if (prevGroundTransportFields.current[key]) {
              // Had a value, now empty, source unchanged: user cleared it
              userCleared.current.add(key);
            }

            if (next && !userCleared.current.has(key)) {
              setValue(`tripItems.${index}.${field}` as any, next, {
                shouldDirty: true,
              });
            }
          }

          prevGroundTransportFields.current[key] = value;
          prevGroundDefaults.current[key] = next;
        });
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tripItemsValues]);

    return (
      <Stack
        horizontal
        tokens={{ childrenGap: 10 }}
        className={styles.container}
        horizontalAlign="end"
      >
        {commonData?.tripSections?.nodes.map((item, index) => {
          const tripItemAdded = tripItemsState.has(item.layoutType!);

          return (
            <Stack
              key={index}
              className={styles.tabContainer}
              onClick={() => {
                const adding = !tripItemsState.has(item.layoutType!);

                const tripDefaultValues = getInitialValues(
                  item.layoutType,
                  selectedTraveler,
                  travelAuthorizationData,
                  tripItemsValues
                );

                const values = getDefaultDistribution(
                  item.id!,
                  userDefaults,
                  true
                );
                if (adding && !inputsDisabled) {
                  const tripItemRow: TripItemFormValues = {
                    ...tripDefaultValues,
                    tripSectionItemId: item.tripSectionItems.nodes?.[0].id,
                    layout: item.layoutType,
                    tripSectionId: item.id,
                    currencyId:
                      tripData?.travelAuthorization?.currency?.id || null,
                    tripDistributions: [{ ...values }],
                  };
                  append(tripItemRow);
                }
                if (!inputsDisabled) {
                  onTripItemsUpdate(
                    tripItemsState.set(item.layoutType!, { ...item }),
                    item.layoutType!,
                    adding
                  );
                }
              }}
            >
              <TooltipHost content={tooltipContent(item, tripItemAdded)}>
                <Stack className={styles.toggleButton}>
                  {tripItemAdded
                    ? getIcon(item.layoutType)
                    : getOutlineIcon(item.layoutType)}
                </Stack>
              </TooltipHost>
            </Stack>
          );
        })}
      </Stack>
    );
  };
